import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useNotices, type NoticeStatus } from "@/hooks/use-notices";
import {
  isLetterTab,
  parseNoticesTab,
  statusesForTab,
  useNoticeTabFilters,
  type NoticesTab,
} from "@/hooks/use-notices-tab";
import { usePermissions } from "@/hooks/use-permissions";
import { useNoticeLadder } from "@/hooks/use-notice-ladders";
import { useNoticeSweepIssues } from "@/hooks/use-notice-sweep-issues";
import { NoticeTabStrip } from "@/components/notices/notice-tab-strip";
import { LettersPanel } from "@/components/notices/letters-panel";
import { TemplatesPanel } from "@/components/notices/templates-panel";
import { LaddersPanel } from "@/components/notices/ladders/ladders-panel";
import { ScheduledPanel } from "@/components/notices/ladders/scheduled-panel";
import { IssuesPanel } from "@/components/notices/ladders/issues-panel";
import {
  formatDrillBound,
  parseDrillStatusSet,
  type NoticeDrill,
} from "@/components/notices/notice-drill";
import { Button, PageHeader, type FilterBarChip } from "@/components/ui";

/** The `/notices` search: the active tab plus the drill axes (`NoticeDrill`). */
interface NoticesSearch extends NoticeDrill {
  /** Which in-page tab to open on; unknown values fall back to the queue. */
  tab?: NoticesTab;
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);

export const Route = createFileRoute("/_authenticated/notices/")({
  component: NoticesIndexPage,
  validateSearch: (search: Record<string, unknown>): NoticesSearch => ({
    tab: parseNoticesTab(search.tab),
    letter_kind: str(search.letter_kind),
    status: parseDrillStatusSet(search.status),
    generated_from: str(search.generated_from),
    generated_to: str(search.generated_to),
    ladder_id: str(search.ladder_id),
  }),
});

/** Statuses the review-queue badge counts — the whole unsent worklist. */
const QUEUE_STATUSES: NoticeStatus[] | undefined = statusesForTab("queue");

/**
 * The notices workspace — ONE tabbed surface over the letter record, the
 * enforcement forecast and the template library. **Review queue** is the
 * call-to-action: every unsent letter, selectable, with the batch approve /
 * send / cancel bar. **Issues** is the sweep's open worklist — every subject
 * whose last attempt failed, with what to fix and a Run now. **Scheduled** is
 * where each enrolled subject stands in its ladder — the forecast, and the
 * surface on which every paused subject is visible with its way out. **Sent**
 * audits the dispatched set, **All notices** searches the whole record
 * including cancelled, rejected and failed letters, **Templates** is the
 * letter-template family library and **Rules** the ladders themselves. All
 * seven are tabs of this route: the page header and the tab strip persist
 * across every one of them.
 *
 * Only the ACTIVE tab's panel is mounted, so a tab fires its own queries and
 * nobody else's — the queue never requests templates and Templates never
 * requests letters. The only reads the shell keeps are the strip's two badge
 * counts — the review-queue depth and the open sweep issues — which have to
 * stay truthful from every tab.
 */
function NoticesIndexPage() {
  const { t } = useTranslation(["notices", "common"]);
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { can } = usePermissions();

  const activeTab = parseNoticesTab(search.tab);
  const tabFilters = useNoticeTabFilters();

  // The create dialog belongs to a VISIT to the Templates tab. The flag is held
  // here because the header owns the button that opens it, but the panel it
  // belongs to unmounts on the way out, so the flag has to come down with it —
  // otherwise returning to the tab reopens the dialog nobody asked for. Keyed on
  // the tab VALUE rather than on the tab-change handler, so a browser Back out
  // of Templates closes it exactly like a click does.
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  useEffect(() => {
    if (activeTab !== "templates") setCreatingTemplate(false);
  }, [activeTab]);

  // The drill axes arriving on the URL. They narrow the list exactly as a
  // filter does, so they are counted as active filters and rendered as
  // removable chips — a drill that returns nothing must never be mistaken for
  // an empty tab.
  const drill = useMemo(
    (): NoticeDrill => ({
      letter_kind: search.letter_kind,
      status: search.status,
      generated_from: search.generated_from,
      generated_to: search.generated_to,
      ladder_id: search.ladder_id,
    }),
    [
      search.letter_kind,
      search.status,
      search.generated_from,
      search.generated_to,
      search.ladder_id,
    ],
  );

  // The ladder drill arrives as an id; the chip has to name the ladder the
  // officer clicked through from. Idle until one is on the URL.
  const drilledLadder = useNoticeLadder(search.ladder_id ?? "");

  /**
   * Drop the named drill axes in ONE navigation, keeping the rest and the tab.
   * Takes a list rather than a single axis because the window is two params
   * behind one chip — clearing them with two calls would have the second read
   * a stale `drill` and put the first back.
   *
   * Dropping a chip REPLACES: it corrects the query the officer is already
   * looking at rather than being a place they would want to come back to.
   */
  const clearDrillAxes = useCallback(
    (...axes: (keyof NoticeDrill)[]) => {
      const next: Record<string, unknown> = activeTab === "queue" ? {} : { tab: activeTab };
      for (const [key, value] of Object.entries(drill)) {
        if (!(axes as string[]).includes(key) && value !== undefined) next[key] = value;
      }
      void navigate({ to: "/notices", search: next as never, replace: true });
    },
    [navigate, activeTab, drill],
  );

  /** Drop every drill axis — the Reset button's drill half. */
  const clearDrill = useCallback(() => {
    void navigate({
      to: "/notices",
      search: (activeTab === "queue" ? {} : { tab: activeTab }) as never,
      replace: true,
    });
  }, [navigate, activeTab]);

  // The tab badge is the WORKLIST DEPTH — how much unsent work is waiting — so
  // it counts the whole queue rather than whatever the active filter bar has
  // narrowed the table to, and it stays truthful from every other tab. It
  // counts letters, folding a letter with a print copy the way the queue's own
  // list does, so the badge and the queue agree.
  const queueCountQuery = useNotices({
    status: QUEUE_STATUSES,
    collapse_pairs: true,
    limit: 1,
    offset: 0,
  });

  // The Issues badge is the open-worklist depth — the sweep's failures that
  // still need an operator — read with one row so it stays cheap from every
  // tab. The sidebar's Notices badge reads the same key.
  const issuesCountQuery = useNoticeSweepIssues({ limit: 1, offset: 0 });

  /**
   * Switching tabs PUSHES a history entry: the officer chose to move, so the
   * browser's Back button has to bring them back to the tab they left. Only
   * the router's own default-tab normalization (`/notices` → `?tab=queue`)
   * replaces, because nobody navigated to the un-normalized URL on purpose.
   *
   * A drill is left behind on the way out — its axes describe one metric's
   * question, which the next tab is not answering.
   */
  const handleTabChange = useCallback(
    (tab: NoticesTab) => {
      void navigate({ to: "/notices", search: tab === "queue" ? {} : { tab } });
    },
    [navigate],
  );

  const drillChips: FilterBarChip[] = useMemo(() => {
    const chips: FilterBarChip[] = [];
    if (drill.letter_kind) {
      chips.push({
        key: "letter_kind",
        label: t("queue.appliedChips.letterKind", {
          value: t(`queue.letterKind.${drill.letter_kind}`, { defaultValue: drill.letter_kind }),
        }),
        onRemove: () => clearDrillAxes("letter_kind"),
      });
    }
    if (drill.status) {
      chips.push({
        key: "status",
        label: t("queue.appliedChips.statusSet", {
          value: drill.status.map((s) => t(`letters.status.${s}`)).join(", "),
        }),
        onRemove: () => clearDrillAxes("status"),
      });
    }
    if (drill.ladder_id) {
      chips.push({
        key: "ladder_id",
        label: t("queue.appliedChips.ladder", {
          value: drilledLadder.data?.name ?? t("queue.appliedChips.ladderUnknown"),
        }),
        onRemove: () => clearDrillAxes("ladder_id"),
      });
    }
    if (drill.generated_from ?? drill.generated_to) {
      chips.push({
        key: "window",
        label: t("queue.appliedChips.window", {
          from: formatDrillBound(drill.generated_from),
          to: formatDrillBound(drill.generated_to),
        }),
        onRemove: () => clearDrillAxes("generated_from", "generated_to"),
      });
    }
    return chips;
  }, [drill, t, clearDrillAxes, drilledLadder.data]);

  // One primary-action slot, filled by whatever the active tab creates. Each arm
  // is offered to the grant the API enforces for that create.
  function headerActionFor(tab: NoticesTab) {
    switch (tab) {
      case "templates":
        return can("notice_templates", "create") ? (
          <Button onClick={() => setCreatingTemplate(true)}>{t("templates.newCta")}</Button>
        ) : undefined;
      // Neither Issues nor Scheduled creates anything — the sweep enrolls the
      // subjects, and an issue is cured at the premise, the template or the
      // ladder and re-run from its own row — so the header slot stays empty
      // rather than offering the letter CTA the letter tabs share.
      case "issues":
      case "scheduled":
        return undefined;
      case "rules":
        return can("notice_ladders", "create") ? (
          <Button onClick={() => void navigate({ to: "/notices/rules/new" })}>
            {t("ladders.newCta")}
          </Button>
        ) : undefined;
      default:
        return can("notices", "create") ? (
          <Button onClick={() => void navigate({ to: "/notices/new" })}>
            {t("queue.newLetter")}
          </Button>
        ) : undefined;
    }
  }
  const headerAction = headerActionFor(activeTab);

  return (
    <div className="space-y-6 p-8">
      <PageHeader title={t("queue.title")} subtitle={t("queue.subtitle")} actions={headerAction} />

      <NoticeTabStrip
        activeTab={activeTab}
        onTabChange={handleTabChange}
        queueCount={queueCountQuery.data?.total_count}
        issuesCount={issuesCountQuery.data?.total_count}
      />

      <div id="notices-tabpanel" role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
        {isLetterTab(activeTab) ? (
          <LettersPanel
            tab={activeTab}
            drill={drill}
            drillChips={drillChips}
            onClearDrill={clearDrill}
            tabFilters={tabFilters}
          />
        ) : activeTab === "issues" ? (
          <IssuesPanel />
        ) : activeTab === "scheduled" ? (
          <ScheduledPanel />
        ) : activeTab === "rules" ? (
          <LaddersPanel />
        ) : (
          <TemplatesPanel creating={creatingTemplate} onCreatingChange={setCreatingTemplate} />
        )}
      </div>
    </div>
  );
}
